import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

export type MeasureFilterState = {
  search: string;
  indicateurIds: string[];
  serviceIds: string[];
  territoryIds: string[];
  empriseKinds: string[];
  units: string[];
  fromYear: string;
  toYear: string;
  periodPreset: string;
  sources: string[];
  qualites: string[];
  statuses: string[];
  dimensionCodes: string[];
  sort: string;
  page: number;
};

function splitCsv(value: string | null): string[] {
  if (!value) return [];
  return value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function applyPreset(preset: string): { fromYear: string; toYear: string } | null {
  switch (preset) {
    case "2005":
      return { fromYear: "2005", toYear: "2005" };
    case "2015":
      return { fromYear: "2015", toYear: "2015" };
    case "2025":
      return { fromYear: "2025", toYear: "2025" };
    case "2005-2015":
      return { fromYear: "2005", toYear: "2015" };
    case "2015-2025":
      return { fromYear: "2015", toYear: "2025" };
    case "2005-2025":
      return { fromYear: "2005", toYear: "2025" };
    case "latest":
      return { fromYear: "2025", toYear: "2025" };
    case "all":
      return { fromYear: "", toYear: "" };
    default:
      return null;
  }
}

export function useMeasureFilters() {
  const [params, setParams] = useSearchParams();

  const state: MeasureFilterState = useMemo(
    () => ({
      search: params.get("q") ?? "",
      indicateurIds: splitCsv(params.get("indicatorId") ?? params.get("indicateurs")),
      serviceIds: splitCsv(params.get("services")),
      territoryIds: splitCsv(params.get("territories")),
      empriseKinds: splitCsv(params.get("empriseKinds")),
      units: splitCsv(params.get("units")),
      fromYear: params.get("from") ?? "",
      toYear: params.get("to") ?? "",
      periodPreset: params.get("period") ?? "all",
      sources: splitCsv(params.get("sources")),
      qualites: splitCsv(params.get("qualites")),
      statuses: splitCsv(params.get("statuses")),
      dimensionCodes: splitCsv(params.get("dimensions")),
      sort: params.get("sort") ?? "period_desc",
      page: Math.max(0, Number(params.get("page") ?? "0") || 0),
    }),
    [params],
  );

  const setState = useCallback(
    (patch: Partial<MeasureFilterState>) => {
      const next = new URLSearchParams(params);
      let merged = { ...state, ...patch };
      if (patch.periodPreset != null) {
        const preset = applyPreset(patch.periodPreset);
        if (preset) merged = { ...merged, ...preset, periodPreset: patch.periodPreset };
      }
      if (merged.search) next.set("q", merged.search);
      else next.delete("q");
      const csv = (key: string, values: string[]) => {
        if (values.length) next.set(key, values.join(","));
        else next.delete(key);
      };
      // Keep mission-compatible indicatorId param for deep links from N2.
      if (merged.indicateurIds.length === 1) {
        next.set("indicatorId", merged.indicateurIds[0]!);
        next.delete("indicateurs");
      } else {
        next.delete("indicatorId");
        csv("indicateurs", merged.indicateurIds);
      }
      csv("services", merged.serviceIds);
      csv("territories", merged.territoryIds);
      csv("empriseKinds", merged.empriseKinds);
      csv("units", merged.units);
      csv("sources", merged.sources);
      csv("qualites", merged.qualites);
      csv("statuses", merged.statuses);
      csv("dimensions", merged.dimensionCodes);
      if (merged.fromYear) next.set("from", merged.fromYear);
      else next.delete("from");
      if (merged.toYear) next.set("to", merged.toYear);
      else next.delete("to");
      if (merged.periodPreset && merged.periodPreset !== "all")
        next.set("period", merged.periodPreset);
      else next.delete("period");
      next.set("sort", merged.sort);
      if (merged.page > 0) next.set("page", String(merged.page));
      else next.delete("page");
      setParams(next, { replace: true });
    },
    [params, setParams, state],
  );

  const reset = useCallback(() => {
    setParams(new URLSearchParams(), { replace: true });
  }, [setParams]);

  const graphqlFilters = {
    search: state.search || null,
    indicateurIds: state.indicateurIds.length ? state.indicateurIds : null,
    serviceIds: state.serviceIds.length ? state.serviceIds : null,
    territoryIds: state.territoryIds.length ? state.territoryIds : null,
    empriseKinds: state.empriseKinds.length ? state.empriseKinds : null,
    units: state.units.length ? state.units : null,
    dateFrom: state.fromYear ? `${state.fromYear}-01-01` : null,
    dateTo: state.toYear ? `${state.toYear}-12-31` : null,
    sources: state.sources.length ? state.sources : null,
    qualites: state.qualites.length ? state.qualites : null,
    statuses: state.statuses.length ? state.statuses : null,
    dimensionCodes: state.dimensionCodes.length ? state.dimensionCodes : null,
  };

  return { state, setState, reset, graphqlFilters, activeCount: params.size };
}
